"use client";

import ConfirmUpdatePlan from "@/components/ConfirmUpdatePlan";
import { useSession } from "@/components/SupabaseProvider";
import { useQueryClient } from "@tanstack/react-query";
import { usePathname, useRouter } from "next/navigation";
import { useFeatureFlagVariantKey, usePostHog } from "posthog-js/react";
import { useContext, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { readCheckoutUrl } from "../lib/checkoutUrl";
import { ModalContext } from "./useModal";
import { PricingExperimentVariant } from "./usePricingExperiment";
import { PROFILE_QUERY_KEY, useProfile } from "./useProfile";
import { useProPriceVariant } from "./useProPriceVariant";
import { hasConsumerPlan } from "../helpers/consumerSubscription";
import { MONTHLY_PRICE } from "../const/publicPricing";
import { PRO_MONTHLY_PRICE } from "../helpers/proPriceExperiment";
import { ScheduleCheckoutContext } from "../components/ScheduleCheckoutProvider";

export type SubscriptionPlan =
  | "basic"
  | "pro"
  | "basic-yearly"
  | "pro-yearly"
  | "pro-plus"
  | "pro-plus-yearly";

type UsePlanCheckoutOptions = {
  plan: SubscriptionPlan;
  experimentVariant?: PricingExperimentVariant;
};

export const usePlanCheckout = ({
  plan,
  experimentVariant,
}: UsePlanCheckoutOptions) => {
  const session = useSession();
  const router = useRouter();
  const pathname = usePathname();
  const { data: profile } = useProfile();
  const [isLoading, setIsLoading] = useState(false);
  const posthog = usePostHog();
  const modal = useContext(ModalContext);
  const scheduleCheckout = useContext(ScheduleCheckoutContext);
  const queryClient = useQueryClient();
  const proPriceVariant = useProPriceVariant();

  const hasConsumerSubscription = hasConsumerPlan(profile);
  const isCurrentPlan = hasConsumerSubscription && profile?.plan === plan;
  const isUpdate = hasConsumerSubscription && profile?.plan !== plan;

  const getClientReferenceId = () => {
    // @ts-ignore
    return window.Rewardful && window.Rewardful.referral;
  };

  const openCheckout = async () => {
    setIsLoading(true);
    posthog?.capture(`Plan Clicked`, {
      plan,
    });
    const referral = getClientReferenceId();

    if (!session) return router.push(`/login?redirect=${pathname}`);
    // Open during the plan click's user gesture, before awaiting the API.
    // The original scheduler stays mounted with its captions and dates.
    const checkoutWindow = scheduleCheckout ? window.open("about:blank", "_blank") : null;
    if (scheduleCheckout && !checkoutWindow) {
      setIsLoading(false);
      return toast.error("Allow pop-ups to open checkout and keep your schedule here.");
    }
    if (checkoutWindow) {
      checkoutWindow.opener = null;
      scheduleCheckout!.start(checkoutWindow);
    }
    let navigated = false;
    const isCheckoutCurrent = () => !scheduleCheckout || Boolean(checkoutWindow && scheduleCheckout.isCurrent(checkoutWindow));
    try {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/billing/subscription`,
        {
          method: isUpdate ? "PATCH" : "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            plan,
            referral: referral || undefined,
            experimentVariant: experimentVariant || undefined,
          }),
        },
      );
      if (!isCheckoutCurrent()) return;

      if (res.status == 401) return toast.error("Please login to continue");
      if (!res.ok) {
        const error = await res.json();
        if (!isCheckoutCurrent()) return;

        if (res.status == 409) {
          // If the user is already on the plan, we need to invalidate the queries
          queryClient.invalidateQueries({ queryKey: [PROFILE_QUERY_KEY] });
          queryClient.invalidateQueries({ queryKey: ["subscriptions"] });
        }

        return toast.error(error.message);
      }
      const checkoutUrl = readCheckoutUrl(await res.json());
      if (!isCheckoutCurrent()) return;
      if (!checkoutUrl) return toast.error("Something went wrong");

      // Before navigating, so the pixel fires. `ttq` is undefined where the
      // analytics scripts don't load (e.g. /embed).
      // The plan's monthly list price (Pro's depends on the buyer's
      // pricing-pro-59 arm), also for yearly plans, as before.
      const monthlyPrice = plan.startsWith("pro-plus")
        ? MONTHLY_PRICE["pro-plus"]
        : plan.startsWith("pro")
          ? PRO_MONTHLY_PRICE[proPriceVariant]
          : MONTHLY_PRICE.basic;
      window.ttq?.track("InitiateCheckout", {
        value: monthlyPrice, // number. The order total. Example: 79.0.
        currency: "USD", // string. The currency of the order. Example: "USD".
        contents: [
          {
            content_id: plan,
            content_type: "product", // string. Either product or product_group.
            content_name: plan, // string. The name of the page or product. Example: "shirt".
            price: monthlyPrice, // number. The price of a single item. Example: 25.
          },
        ],
      });
      // The top window, as Stripe.js's redirectToCheckout did: the plans
      // dialog also opens in the /embed player, inside someone else's page.
      if (checkoutWindow) checkoutWindow.location.href = checkoutUrl;
      else (window.top ?? window).location.href = checkoutUrl;
      navigated = true;
    } catch (error) {
      if (!isCheckoutCurrent()) return;
      console.error(error);
      toast.error("Something went wrong");
    } finally {
      const current = isCheckoutCurrent();
      if (checkoutWindow && !navigated) {
        checkoutWindow.close();
        scheduleCheckout?.cancel(checkoutWindow);
      }
      if (current) setIsLoading(false);
    }
  };

  const handleCheckout = async () => {
    if (isUpdate) {
      posthog?.capture("Upgrade Plan Dialog Showed", {
        source: "pricing_card",
      });
      return modal.open(
        <ConfirmUpdatePlan
          targetPlan={plan}
          onUpdate={() => {
            modal.close();
          }}
        />,
      );
    }

    return openCheckout();
  };

  const cardRequiredFreeTrialKey = useFeatureFlagVariantKey(
    "card-required-free-trial",
  );
  const isCardRequiredFreeTrialAllPlans =
    cardRequiredFreeTrialKey === "all_plans";
  const isCardRequiredFreeTrialBasicOnly =
    cardRequiredFreeTrialKey === "basic_only";

  const ctaLabel = useMemo(() => {
    if (isCurrentPlan) return "Current Plan";
    if (isUpdate) return "Select";
    if (isCardRequiredFreeTrialAllPlans) return "Start 3-day free trial";
    if (isCardRequiredFreeTrialBasicOnly && plan.includes("basic"))
      return "Start 3-day free trial";
    return "Get started";
  }, [
    isCurrentPlan,
    isUpdate,
    plan,
    isCardRequiredFreeTrialAllPlans,
    isCardRequiredFreeTrialBasicOnly,
  ]);

  return { handleCheckout, ctaLabel, isLoading, isCurrentPlan, isUpdate };
};
